/**
 * Confirm-entry landing page — /confirm/:token
 *
 * Pre-regatta confirmation path (SPEC-Pre-Regatta-Confirmation §F3).
 * An organizer has imported a roster, matched each row to an entry, and
 * sent "confirm your entry" emails.  The skipper clicks the link and lands here.
 *
 * Mechanically identical to InviteLanding (/invite/:token): resolves the
 * same /api/v4/invitations/:token endpoint, submits to the same
 * /api/v4/invitations/:token/claim endpoint, and stores the JWT.  Only
 * copy and button labels differ.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { ResolveInviteResponse, ClaimInviteResponse } from '@regatta-replay/types';
import { formatSailNumber } from '@regatta-replay/types';
import { api, ApiError } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  PhoneVerificationStep,
  type PhoneVerificationResult,
} from '@/components/claim/PhoneVerificationStep';

type PageState = 'loading' | 'valid' | 'invalid' | 'claiming' | 'error';

export function ConfirmLanding() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);

  const [pageState, setPageState] = useState<PageState>('loading');
  const [invite, setInvite] = useState<ResolveInviteResponse | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [phoneVerify, setPhoneVerify] = useState<PhoneVerificationResult | null>(null);

  useEffect(() => {
    if (!token) {
      setPageState('invalid');
      setErrorMessage('No confirmation token provided.');
      return;
    }

    let cancelled = false;

    async function resolve() {
      try {
        const data = await api.get<ResolveInviteResponse>(`/invitations/${token}`);
        if (cancelled) return;

        if (!data.valid) {
          setPageState('invalid');
          if (data.expired) {
            setErrorMessage('This confirmation link has expired.');
          } else if (data.alreadyClaimed) {
            setErrorMessage('Your entry has already been confirmed.');
          } else {
            setErrorMessage('This confirmation link is no longer valid.');
          }
          return;
        }

        setInvite(data);
        setDisplayName(data.skipperName ?? '');
        setPageState('valid');
      } catch {
        if (cancelled) return;
        setPageState('error');
        setErrorMessage('Failed to load confirmation. Please try again later.');
      }
    }

    resolve();
    return () => { cancelled = true; };
  }, [token]);

  async function handleConfirm() {
    if (!token || !displayName.trim()) return;
    setPageState('claiming');
    try {
      const result = await api.post<ClaimInviteResponse>(
        `/invitations/${token}/claim`,
        {
          displayName: displayName.trim(),
          ...(phoneVerify?.verifyToken ? { verifyToken: phoneVerify.verifyToken } : {}),
          ...(phoneVerify?.notifyOptIn ? { notifyOptIn: true } : {}),
          ...(phoneVerify?.notifyConsentText ? { notifyConsentText: phoneVerify.notifyConsentText } : {}),
        },
      );
      login(result.token, result.user);
      navigate(`/regatta/${result.regattaSlug}`);
    } catch (err) {
      setPageState('valid');
      if (err instanceof ApiError && err.status === 400) {
        const msg = (err as ApiError & { body?: { message?: string } }).body?.message ?? '';
        if (msg.toLowerCase().includes('phone verification')) {
          setPhoneVerify(null);
          setErrorMessage('Phone verification is required. Please verify your phone number above.');
        } else {
          setErrorMessage(msg || 'Failed to confirm entry. Please try again.');
        }
      } else {
        setErrorMessage('Failed to confirm entry. Please try again.');
      }
    }
  }

  return (
    <div className="flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">

        {/* Loading */}
        {pageState === 'loading' && (
          <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm" data-testid="confirm-loading">
            <Skeleton className="mx-auto mb-4 h-6 w-56" />
            <Skeleton className="mx-auto mb-2 h-5 w-64" />
            <Skeleton className="mx-auto mb-6 h-5 w-40" />
            <Skeleton className="mb-2 h-4 w-24" />
            <Skeleton className="mb-4 h-10 w-full" />
            <Skeleton className="mb-2 h-4 w-16" />
            <Skeleton className="mb-6 h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}

        {/* Invalid / Expired */}
        {(pageState === 'invalid' || pageState === 'error') && (
          <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm text-center" data-testid="confirm-error">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-navy-900">Link not valid</h1>
            <p className="mt-2 text-sm text-muted-foreground">{errorMessage}</p>
          </div>
        )}

        {/* Confirmation form */}
        {(pageState === 'valid' || pageState === 'claiming') && invite && (
          <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm" data-testid="confirm-form">
            <div className="text-center mb-6">
              <h1 className="text-xl font-semibold text-navy-900">Confirm Your Entry</h1>
              <p className="mt-2 text-base font-medium text-navy-900">{invite.regattaName}</p>
              <p className="text-sm text-muted-foreground">
                {invite.boatName}
                {invite.sailNumber ? ` (${formatSailNumber(invite.sailNumber)})` : ''}
              </p>
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); handleConfirm(); }}
              className="space-y-4"
            >
              <div>
                <Label htmlFor="displayName">Your Name</Label>
                <Input
                  id="displayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your full name"
                  required
                  data-testid="display-name-input"
                />
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={invite.email ?? ''}
                  readOnly
                  className="bg-gray-50"
                  data-testid="email-input"
                />
              </div>

              {import.meta.env.VITE_ENABLE_PHONE_VERIFY === 'true' && (
                <div className="rounded-md border border-gray-200 p-4" data-testid="phone-verify-section">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Race alerts
                  </p>
                  {phoneVerify === null ? (
                    <PhoneVerificationStep
                      onVerified={(result) => setPhoneVerify(result)}
                      onSkip={() => setPhoneVerify({ notifyOptIn: false })}
                    />
                  ) : phoneVerify.verifyToken ? (
                    <PhoneVerificationStep
                      onVerified={(result) => setPhoneVerify(result)}
                      onSkip={() => setPhoneVerify({ notifyOptIn: false })}
                      existingVerifiedPhone="verified"
                    />
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Skipped — you can add a phone later in settings.
                    </p>
                  )}
                </div>
              )}

              {errorMessage && pageState === 'valid' && (
                <p className="text-sm text-destructive">{errorMessage}</p>
              )}

              <Button
                type="submit"
                className="w-full bg-brand-blue !text-navy-900 hover:bg-brand-dark"
                disabled={pageState === 'claiming' || !displayName.trim()}
                data-testid="confirm-button"
              >
                {pageState === 'claiming' ? 'Confirming…' : 'Confirm My Entry'}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
